import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Form, Input, Modal, Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { CheckOutlined, CopyOutlined, DeleteOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons'
import { useCookies } from 'react-cookie'
import { useNavigate } from 'react-router-dom'
import { ContentHeader, PaddingBox, PageBreadcrumb, PageTitle, TabHeader } from '../../components/Wrapper'
import { RequireRole } from '../../components/RequireRole'
import {
  decodeAccessToken,
  deleteOrganization,
  getAdminUuidByEmail,
  getOrganizations,
  OrganizationSummary,
  requestVerificationCode,
  signUp,
  verifyCode,
} from '../../services/admin-auth-service'

interface AddOrganizationFormValues {
  username: string
  email: string
}

const getColumns = (
  deletingId: string | null,
  onDelete: (org: OrganizationSummary) => void,
  onManageDoctors: (org: OrganizationSummary) => void
): ColumnsType<OrganizationSummary> => [
  { title: 'Organization Name', dataIndex: 'username', key: 'username' },
  { title: 'Email', dataIndex: 'email', key: 'email' },
  {
    title: 'Created',
    dataIndex: 'creationTimestamptz',
    key: 'creationTimestamptz',
    render: (value: string) => (value ? dayjs(value).format('DD MMM YYYY') : '-'),
  },
  {
    title: '',
    key: 'manageDoctors',
    width: '160px',
    render: (_, record) => (
      <Button type='link' icon={<TeamOutlined />} onClick={() => onManageDoctors(record)}>
        Manage Doctors
      </Button>
    ),
  },
  {
    title: '',
    key: 'action',
    width: '120px',
    render: (_, record) => (
      <Button
        danger
        type='text'
        icon={<DeleteOutlined />}
        loading={deletingId === record.id}
        onClick={() => onDelete(record)}>
        Delete
      </Button>
    ),
  },
]

const ManageOrganizations: React.FC = () => {
  const [cookies] = useCookies(['loginToken'])
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [adminUuid, setAdminUuid] = useState<string | null>(null)
  const [adminEmail, setAdminEmail] = useState<string | null>(null)
  const [adminUuidError, setAdminUuidError] = useState('')
  const [copied, setCopied] = useState(false)
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([])
  const [organizationsLoading, setOrganizationsLoading] = useState(true)
  const [organizationsError, setOrganizationsError] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<OrganizationSummary | null>(null)
  const [pendingDeleteOrg, setPendingDeleteOrg] = useState<OrganizationSummary | null>(null)
  const [sendingCode, setSendingCode] = useState(false)
  const [verifyCodeValue, setVerifyCodeValue] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [verifyError, setVerifyError] = useState('')
  const [form] = Form.useForm<AddOrganizationFormValues>()

  const copyAdminUuid = useCallback(() => {
    if (!adminUuid) return
    navigator.clipboard.writeText(adminUuid)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }, [adminUuid])

  const refreshOrganizations = useCallback(async (managingAdminUuid: string | null) => {
    setOrganizationsLoading(true)
    setOrganizationsError('')
    try {
      const all = await getOrganizations()
      setOrganizations(managingAdminUuid ? all.filter((org) => org.adminUuid === managingAdminUuid) : all)
    } catch (e: any) {
      setOrganizationsError(e.message)
    } finally {
      setOrganizationsLoading(false)
    }
  }, [])

  const handleDeleteClick = useCallback((org: OrganizationSummary) => {
    setOrganizationsError('')
    setDeleteTarget(org)
  }, [])

  const cancelDeleteTarget = useCallback(() => {
    setDeleteTarget(null)
  }, [])

  const proceedToVerification = useCallback(async () => {
    if (!deleteTarget || !adminEmail) {
      setOrganizationsError('Unable to identify your email for verification.')
      return
    }
    const org = deleteTarget
    setDeleteTarget(null)
    setVerifyError('')
    setVerifyCodeValue('')
    setPendingDeleteOrg(org)
    setSendingCode(true)
    try {
      await requestVerificationCode(adminEmail, 'ROLE_ADMIN')
    } catch (e: any) {
      setVerifyError(e.message)
    } finally {
      setSendingCode(false)
    }
  }, [deleteTarget, adminEmail])

  const cancelDelete = useCallback(() => {
    setPendingDeleteOrg(null)
    setVerifyCodeValue('')
    setVerifyError('')
  }, [])

  const confirmDeleteWithCode = useCallback(async () => {
    if (!pendingDeleteOrg || !adminEmail) return
    setVerifyError('')
    setVerifying(true)
    try {
      await verifyCode(adminEmail, 'ROLE_ADMIN', verifyCodeValue)
      setDeletingId(pendingDeleteOrg.id)
      await deleteOrganization(pendingDeleteOrg.id)
      refreshOrganizations(adminUuid)
      setPendingDeleteOrg(null)
      setVerifyCodeValue('')
    } catch (e: any) {
      setVerifyError(`${e.message} Please try again.`)
    } finally {
      setVerifying(false)
      setDeletingId(null)
    }
  }, [pendingDeleteOrg, adminEmail, adminUuid, verifyCodeValue, refreshOrganizations])

  useEffect(() => {
    const lookupAdminUuid = async () => {
      const decoded = decodeAccessToken(cookies.loginToken ?? '')
      if (!decoded?.email) {
        setAdminUuidError('Unable to identify the logged-in admin.')
        setOrganizationsLoading(false)
        return
      }
      setAdminEmail(decoded.email)
      try {
        const uuid = await getAdminUuidByEmail(decoded.email)
        if (!uuid) {
          setAdminUuidError('Could not find your admin account UUID.')
          setOrganizationsLoading(false)
          return
        }
        setAdminUuid(uuid)
        refreshOrganizations(uuid)
      } catch (e: any) {
        setAdminUuidError(e.message)
        setOrganizationsLoading(false)
      }
    }
    lookupAdminUuid()
  }, [cookies.loginToken, refreshOrganizations])

  const openModal = useCallback(() => {
    setError('')
    setSuccess('')
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    form.resetFields()
  }, [form])

  const onSubmit = useCallback(
    async (values: AddOrganizationFormValues) => {
      if (!adminUuid) {
        setError('Your admin account UUID could not be identified. Please try again later.')
        return
      }
      setError('')
      setLoading(true)
      try {
        await signUp({
          username: values.username,
          email: values.email,
          role: 'ROLE_ORGANIZATION',
          adminUuid,
        })
        setSuccess(`Organization "${values.username}" was created successfully.`)
        form.resetFields()
        refreshOrganizations(adminUuid)
        setTimeout(() => {
          setIsModalOpen(false)
        }, 1200)
      } catch (e: any) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    },
    [form, adminUuid, refreshOrganizations]
  )

  return (
    <div className='dash-board-page'>
      <ContentHeader white={false} border>
        <PageBreadcrumb path={[{ label: 'Home', href: '/' }, 'Manage Organizations']} />
        <TabHeader padding={false}>
          <PageTitle text='Manage Organizations' />
          <Button type='primary' icon={<PlusOutlined />} onClick={openModal}>
            Add an Organization
          </Button>
        </TabHeader>
      </ContentHeader>

      <PaddingBox white>
        {organizationsError && (
          <Alert style={{ marginBottom: 16 }} type='error' showIcon message={organizationsError} />
        )}
        <Table
          columns={getColumns(deletingId, handleDeleteClick, (org) => navigate(`/organizations/${org.id}/doctors`))}
          dataSource={organizations}
          loading={organizationsLoading}
          rowKey={(record) => record.id}
          locale={{ emptyText: 'No organizations yet. Click "Add an Organization" to create one.' }}
          pagination={false}
        />
      </PaddingBox>

      <Modal
        title='Add an Organization'
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose>
        {error && <Alert style={{ marginBottom: 16 }} showIcon message={error} type='error' />}
        {success && <Alert style={{ marginBottom: 16 }} showIcon message={success} type='success' />}
        {adminUuidError && <Alert style={{ marginBottom: 16 }} showIcon message={adminUuidError} type='warning' />}
        <Form form={form} layout='vertical' onFinish={onSubmit} autoComplete='off'>
          <Form.Item
            name='username'
            label='Organization Name'
            rules={[{ required: true, message: 'Enter an organization name.' }]}>
            <Input placeholder='SGH' />
          </Form.Item>

          <Form.Item
            name='email'
            label='Email'
            rules={[{ required: true, type: 'email', message: 'Enter a valid email.' }]}>
            <Input placeholder='sgh@nus.edu.sg' />
          </Form.Item>

          <Form.Item label='Admin User UUID' extra='This is your own admin account, filled in automatically.'>
            <Input
              value={adminUuid ?? 'Loading…'}
              disabled
              suffix={
                adminUuid && (
                  <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                    <Button
                      type='text'
                      size='small'
                      icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                      onClick={copyAdminUuid}
                    />
                  </Tooltip>
                )
              }
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button block type='primary' htmlType='submit' loading={loading} disabled={!adminUuid}>
              Create Organization
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title='Delete this organization?'
        open={!!deleteTarget}
        onCancel={cancelDeleteTarget}
        okText='Send code'
        okButtonProps={{ danger: true }}
        onOk={proceedToVerification}
        centered>
        <p>
          {`This will permanently delete "${deleteTarget?.username}". This cannot be undone. We'll email you a code to confirm.`}
        </p>
      </Modal>

      <Modal
        title='Confirm deletion'
        open={!!pendingDeleteOrg}
        onCancel={cancelDelete}
        centered
        footer={null}
        destroyOnClose>
        {verifyError && <Alert style={{ marginBottom: 16 }} showIcon message={verifyError} type='error' />}
        {sendingCode ? (
          <Alert showIcon message={`Sending a verification code to ${adminEmail}…`} type='info' />
        ) : (
          <>
            <Alert
              style={{ marginBottom: 16 }}
              showIcon
              type='info'
              message={`A verification code was sent to ${adminEmail}. Enter it below to permanently delete "${pendingDeleteOrg?.username}".`}
            />
            <Input
              placeholder='6-digit code'
              maxLength={6}
              value={verifyCodeValue}
              onChange={(e) => setVerifyCodeValue(e.target.value)}
              onPressEnter={confirmDeleteWithCode}
              style={{ marginBottom: 16 }}
            />
            <Button
              block
              danger
              type='primary'
              loading={verifying}
              disabled={verifyCodeValue.length === 0}
              onClick={confirmDeleteWithCode}>
              Verify & Delete
            </Button>
          </>
        )}
      </Modal>
    </div>
  )
}

const ManageOrganizationsPage: React.FC = () => (
  <RequireRole allow={['ROLE_ADMIN']}>
    <ManageOrganizations />
  </RequireRole>
)

export default ManageOrganizationsPage
